import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Trash2, KeyRound, Lock, X } from "lucide-react";

const PIN_DEVICE_KEY = "cc_pin_device_id";
const PIN_EMAIL_KEY = "cc_pin_email";
const PIN_NAME_KEY = "cc_pin_name";
const PIN_ROLE_KEY = "cc_pin_role";

export function getSavedPinDevice() {
  const device_id = localStorage.getItem(PIN_DEVICE_KEY);
  const email = localStorage.getItem(PIN_EMAIL_KEY);
  const name = localStorage.getItem(PIN_NAME_KEY);
  const role = localStorage.getItem(PIN_ROLE_KEY);
  if (device_id && email) return { device_id, email, name, role };
  return null;
}

export function saveSavedPinDevice({ device_id, email, name, role }) {
  localStorage.setItem(PIN_DEVICE_KEY, device_id);
  localStorage.setItem(PIN_EMAIL_KEY, email);
  if (name) localStorage.setItem(PIN_NAME_KEY, name);
  if (role) localStorage.setItem(PIN_ROLE_KEY, role);
}

export function clearSavedPinDevice() {
  localStorage.removeItem(PIN_DEVICE_KEY);
  localStorage.removeItem(PIN_EMAIL_KEY);
  localStorage.removeItem(PIN_NAME_KEY);
  localStorage.removeItem(PIN_ROLE_KEY);
}

function PinModal({ title, subtitle, onSubmit, onCancel, submitting }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const isValid = pin.length === 4 && confirm.length === 4;
  const submit = () => {
    if (pin !== confirm) { toast.error("PINs don't match."); return; }
    onSubmit(pin);
  };
  const input = "w-full h-14 text-center tracking-[0.6em] text-2xl font-bold rounded-xl border border-slate-300 focus:border-[#C86D51] outline-none bg-white";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="pin-setup-modal">
      <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-black text-slate-900">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onCancel} className="p-2 -m-2 text-slate-400" data-testid="pin-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">New 4-digit PIN</label>
          <input data-testid="pin-input-new" inputMode="numeric" pattern="[0-9]*" autoComplete="new-password"
            value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4} className={input} placeholder="••••" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Confirm PIN</label>
          <input data-testid="pin-input-confirm" inputMode="numeric" pattern="[0-9]*" autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4} className={input} placeholder="••••" />
        </div>
        <button data-testid="pin-modal-submit" disabled={!isValid || submitting} onClick={submit}
          className="w-full py-3.5 rounded-xl copper-gradient text-white font-bold shadow-md active:scale-[0.98] transition-transform disabled:opacity-50">
          {submitting ? "Saving…" : "Save PIN"}
        </button>
        <p className="text-xs text-slate-400 text-center">Your PIN unlocks this device only. It's stored securely and never leaves your browser as plain text.</p>
      </div>
    </div>
  );
}

export function PinSetupCard() {
  const [saved, setSaved] = useState(() => getSavedPinDevice());
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (!saved?.device_id) return;
    api.get(`/auth/pin/status`, { params: { device_id: saved.device_id } })
      .then((r) => {
        if (!r.data?.exists) { clearSavedPinDevice(); setSaved(null); }
        else setLocked(!!r.data.locked);
      }).catch(() => {});
  }, [saved?.device_id]);

  const setup = async (pin) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/pin/setup", { pin });
      saveSavedPinDevice({ device_id: data.device_id, email: data.email, name: data.name, role: data.role });
      setSaved(getSavedPinDevice());
      setLocked(false);
      setModalOpen(false);
      toast.success("PIN set. You can now sign in with just your PIN on this device.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const remove = async () => {
    if (!saved?.device_id) return;
    if (!window.confirm("Remove PIN from this device? You'll need to log in with your password next time.")) return;
    try {
      await api.delete(`/auth/pin/${saved.device_id}`);
    } catch (e) { /* device_id already gone is fine */ }
    clearSavedPinDevice();
    setSaved(null);
    toast.success("PIN removed from this device.");
  };

  return (
    <section data-testid="pin-setup-card" className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#F7E7DE] flex items-center justify-center flex-shrink-0">
          <KeyRound className="h-5 w-5 text-[#9A462B]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-bold text-slate-900">Quick PIN Login</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            {saved ? "Enabled on this device — sign in with just 4 digits." : "Set a 4-digit PIN on this device for faster sign-in on mobile."}
          </p>
        </div>
      </div>
      {saved ? (
        <>
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
            <ShieldCheck className="h-4 w-4" /> Active for <b>{saved.email}</b>
            {locked && <span className="ml-auto flex items-center gap-1 text-red-600 font-bold"><Lock className="h-3.5 w-3.5" /> Locked</span>}
          </div>
          {locked && <p className="text-xs text-slate-500">Log in with your password once to unlock it.</p>}
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="pin-change-button" onClick={() => setModalOpen(true)}
              className="py-3 rounded-xl border border-slate-200 font-bold text-slate-700 text-sm active:scale-[0.98] transition-transform">
              Change PIN
            </button>
            <button data-testid="pin-remove-button" onClick={remove}
              className="py-3 rounded-xl border border-red-200 text-red-600 font-bold text-sm flex items-center justify-center gap-1 active:scale-[0.98] transition-transform">
              <Trash2 className="h-4 w-4" /> Remove
            </button>
          </div>
        </>
      ) : (
        <button data-testid="pin-setup-button" onClick={() => setModalOpen(true)}
          className="w-full py-3 rounded-xl copper-gradient text-white font-bold shadow-md active:scale-[0.98] transition-transform">
          Set up PIN
        </button>
      )}
      {modalOpen && (
        <PinModal
          title={saved ? "Change PIN" : "Set up a 4-digit PIN"}
          subtitle="Only this device will remember your PIN."
          onSubmit={setup}
          onCancel={() => setModalOpen(false)}
          submitting={submitting}
        />
      )}
    </section>
  );
}

export default PinSetupCard;
